/*
 * Withdraw: an amount (prefilled with everything available), the bank account
 * it goes to, and a confirm. POST /withdrawals {amount: "400.00"}. The limits
 * are checked here in whole paise, and again by the server, whose 409/403
 * sentences are shown as-is. The amount is GST-inclusive; GST and TDS come off
 * it, and the preview below uses the server's rates and rounding.
 */

import { useState } from "react";
import { errorMessage, wallet as walletApi, type PayoutAccountMasked, type Wallet, type Withdrawal } from "../api/client";
import { Dialog } from "./bits";
import { fromPaise, inr, rateLabel, toPaise, withdrawalBreakdown } from "./format";
import { Landmark } from "./icons";

const AMOUNT = /^\d+(\.\d{0,2})?$/;

export function bankLine(a: Pick<PayoutAccountMasked, "bank_name" | "account_last4" | "ifsc">): string {
  return `${a.bank_name ? `${a.bank_name} · ` : ""}••••${a.account_last4} · ${a.ifsc}`;
}

export default function WithdrawDialog({
  wallet,
  onClose,
  onDone,
}: {
  wallet: Wallet & { payout_account: PayoutAccountMasked };
  onClose: () => void;
  onDone: (w: Withdrawal) => void;
}) {
  const maxPaise = toPaise(wallet.available);
  const [amount, setAmount] = useState(wallet.available);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clean = amount.replace(/,/g, "").trim();
  const paise = AMOUNT.test(clean) ? toPaise(clean) : NaN;
  const problem = !clean
    ? "Enter an amount."
    : Number.isNaN(paise)
      ? "Use numbers only, with up to 2 decimals."
      : paise <= 0
        ? "Enter an amount greater than zero."
        : paise > maxPaise
          ? `You can withdraw up to ${inr(wallet.available)}.`
          : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (problem) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await walletApi.withdraw(fromPaise(paise)));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const acct = wallet.payout_account;
  const { gst_rate, tds_rate, pan_on_file } = wallet.deductions;
  const split = problem ? null : withdrawalBreakdown(paise, gst_rate, tds_rate);

  return (
    <Dialog
      title="Withdraw to your bank"
      description={`Available now: ${inr(wallet.available)}. Wealthoak's finance team approves and pays each request by bank transfer.`}
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <form onSubmit={submit} noValidate>
        <div className="field">
          <label className="field__label" htmlFor="wd-amount">Amount (₹)</label>
          <div className="wd-amount">
            <span aria-hidden="true">₹</span>
            <input
              id="wd-amount"
              className="input num"
              inputMode="decimal"
              autoComplete="off"
              value={amount}
              aria-invalid={Boolean(problem) && clean !== ""}
              aria-describedby="wd-amount-help"
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ""))}
              data-autofocus
            />
            <button
              type="button"
              className="button button--secondary button--sm"
              onClick={() => setAmount(wallet.available)}
              disabled={clean === wallet.available}
            >
              Withdraw all
            </button>
          </div>
          <p id="wd-amount-help" className={problem && clean !== "" ? "field__error" : "field__hint"}>
            {problem && clean !== "" ? problem : `Up to ${inr(wallet.available)}.`}
          </p>
        </div>

        <dl className="wd-split" aria-label="What you receive" aria-live="polite">
          <div>
            <dt>Withdrawal amount</dt>
            <dd className="num">{split ? inr(fromPaise(paise)) : "—"}</dd>
          </div>
          <div>
            <dt>GST ({rateLabel(gst_rate)}, included in the amount)</dt>
            <dd className="num">{split ? `− ${inr(fromPaise(split.gst))}` : "—"}</dd>
          </div>
          <div>
            <dt>
              TDS ({rateLabel(tds_rate)} of {split ? inr(fromPaise(split.taxable)) : "the taxable amount"})
            </dt>
            <dd className="num">{split ? `− ${inr(fromPaise(split.tds))}` : "—"}</dd>
          </div>
          <div className="wd-split__net">
            <dt>You receive</dt>
            <dd className="num">{split ? inr(fromPaise(split.net)) : "—"}</dd>
          </div>
        </dl>
        {!pan_on_file && (
          <p className="field__hint wd-split__note">
            No PAN on your profile, so TDS is {rateLabel(tds_rate)}. Ask Wealthoak to add your PAN to bring it down to 2%.
          </p>
        )}

        <div className="wd-dest">
          <span className="wd-dest__icon" aria-hidden="true">
            <Landmark size={18} />
          </span>
          <span>
            <small>Paid to</small>
            <strong>{acct.account_holder}</strong>
            <span className="mono">{bankLine(acct)}</span>
          </span>
        </div>

        {error && <div className="form-error" role="alert">{error}</div>}

        <div className="form-actions">
          <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button button--primary" disabled={busy || Boolean(problem)}>
            {busy ? "Requesting…" : split ? `Request ${inr(fromPaise(paise))}` : "Request withdrawal"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
